/**
 * Draft ↔ stored 保障配置 v2 (guard-v2). A drawer edits one row's `policy`:
 * the SKU row (signals + probe profiles) or one org×SKU row (overrides). The
 * draft is plain text per field; a PUT carries only the groups that differ
 * from the draft the row was opened with, and the result is checked with the
 * contracts' own `applyGuardPolicyPatch` before it is sent — the page holds no
 * second set of combination rules. Legacy `no_response_cutoff_ms` is folded
 * into the stream row for display only, until that row is edited.
 */
import {
  GUARD_PROBE_PROFILES,
  GUARD_SIGNAL_KINDS,
  applyGuardPolicyPatch,
  parseGuardPolicyRead,
  resolveGuardPolicy,
  resolveNoResponseCutoffMs,
  type GuardMode,
  type GuardParseResult,
  type GuardProbeProfile,
  type GuardSignalKind,
  type GuardSignalSection,
  type GuardTripSpec,
  type StoredGuardPolicy,
} from '@nadoutong/contracts';
import type { I18nKey, TParams } from '../../../i18n/index.ts';
import {
  CLEAN_RANGE,
  GUARD_SIGNAL_VIEWS,
  PROFILE_DEFAULTS,
  PROFILE_RANGES,
  RETRY_AFTER_CAP_RANGE,
  TRIP_RANGES,
  signalView,
  thresholdRangeSeconds,
  type GuardRange,
} from './guardSignals.ts';

export type GuardDrawerScope = 'sku' | 'org';

export interface TripDraft {
  readonly mode: GuardMode;
  readonly window: string;
  readonly hits: string;
  /** Percent text (0–100). */
  readonly ratio: string;
  readonly hold: string;
}

export interface SkuSignalDraft {
  readonly walk: GuardMode;
  /** Seconds text. */
  readonly threshold: string;
  readonly retryAfterReasoning: boolean;
  /** S4 `chat_lane`: the act walk also covers the chat / completions lane. */
  readonly chatLane: boolean;
  /** Seconds text; empty = no cap. */
  readonly cap: string;
  readonly trip: TripDraft;
  readonly recoverProfile: GuardProbeProfile;
  readonly clean: string;
}

export interface OrgSignalDraft {
  readonly disposal: 'inherit' | 'custom';
  readonly walk: GuardMode;
  readonly threshold: string;
  readonly retryAfterReasoning: boolean;
  readonly chatLane: boolean;
  readonly globalTrip: 'inherit' | 'own';
  readonly borrow: boolean;
  readonly trip: TripDraft;
}

export interface ProfileDraft {
  readonly enabled: boolean;
  readonly interval: string;
  readonly shots: string;
  readonly timeout: string;
  readonly minStream: string;
  readonly maxGap: string;
}

export interface SkuDraft {
  readonly scope: 'sku';
  readonly signals: Readonly<Record<GuardSignalKind, SkuSignalDraft>>;
  readonly profiles: Readonly<Record<GuardProbeProfile, ProfileDraft>>;
}

export interface OrgDraft {
  readonly scope: 'org';
  readonly signals: Readonly<Record<GuardSignalKind, OrgSignalDraft>>;
}

export type GuardDraft = SkuDraft | OrgDraft;

export interface GuardIssue {
  readonly key: I18nKey;
  readonly params: TParams;
}

/**
 * The wire `policy` patch (contracts `GuardPolicyPatch` semantics). Values are
 * built from operator text, so the write face and {@link previewPolicy} — not
 * this type — are what make them valid.
 */
export interface GuardPolicyPatchBody {
  readonly signals?: Readonly<Partial<Record<GuardSignalKind, Readonly<Record<string, unknown>>>>>;
  readonly probe_profiles?: Readonly<Partial<Record<GuardProbeProfile, Readonly<Record<string, unknown>> | null>>>;
}

export interface GuardPutBody {
  readonly scope: 'sku' | 'org_sku';
  readonly sku_id: string;
  readonly org_id?: string;
  readonly policy: GuardPolicyPatchBody;
}

export function asStoredPolicy(raw: unknown): StoredGuardPolicy {
  return parseGuardPolicyRead(raw);
}

function secondsText(ms: number): string {
  return String(ms / 1000);
}

function ratioText(ratio: number): string {
  return String(Math.round(ratio * 10_000) / 100);
}

function has(section: GuardSignalSection | undefined, field: keyof GuardSignalSection): boolean {
  return section !== undefined && field in section;
}

function tripDraftOf(trip: GuardTripSpec | null | undefined, kind: GuardSignalKind): TripDraft {
  const defaults = signalView(kind).tripDefaults;
  const pick = (value: number | undefined, fallback: number): string => String(value ?? fallback);
  if (trip === null || trip === undefined) {
    return {
      mode: 'off',
      window: String(defaults.window_s),
      hits: String(defaults.min_hits),
      ratio: String(defaults.ratio_pct),
      hold: String(defaults.max_hold_s),
    };
  }
  return {
    mode: trip.mode,
    window: pick(trip.window_s, defaults.window_s),
    hits: pick(trip.min_hits, defaults.min_hits),
    ratio: trip.min_ratio === undefined ? String(defaults.ratio_pct) : ratioText(trip.min_ratio),
    hold: pick(trip.max_hold_s, defaults.max_hold_s),
  };
}

/** The legacy column decides the stream cut while the stream row says nothing (#2744 merge rule). */
export function streamFromLegacy(stored: StoredGuardPolicy, legacyStreamCutoffMs: number | null): boolean {
  const section = stored.signals?.first_content_stream;
  return legacyStreamCutoffMs !== null && !has(section, 'threshold_ms') && !has(section, 'walk');
}

function skuSignalDraft(
  kind: GuardSignalKind,
  stored: StoredGuardPolicy,
  legacyStreamCutoffMs: number | null,
): SkuSignalDraft {
  const view = signalView(kind);
  const section = stored.signals?.[kind];
  const legacy = kind === 'first_content_stream' && streamFromLegacy(stored, legacyStreamCutoffMs);
  const thresholdMs = section?.threshold_ms;
  const threshold = legacy && legacyStreamCutoffMs !== null
    ? secondsText(legacyStreamCutoffMs)
    : typeof thresholdMs === 'number'
      ? secondsText(thresholdMs)
      : view.defaultThresholdS === null ? '' : String(view.defaultThresholdS);
  const cap = section?.retry_after_cap_s;
  return {
    walk: legacy ? 'act' : (section?.walk ?? 'off'),
    threshold,
    retryAfterReasoning: section?.retry_after_reasoning === true,
    chatLane: section?.chat_lane === true,
    cap: typeof cap === 'number' ? String(cap) : '',
    trip: tripDraftOf(section?.trip, kind),
    recoverProfile: section?.recover?.profile ?? view.spec.defaultRecoverProfile,
    clean: String(section?.recover?.clean ?? view.defaultClean),
  };
}

function profileDraftOf(stored: StoredGuardPolicy, profile: GuardProbeProfile): ProfileDraft {
  const short = stored.probe_profiles?.short;
  const long = stored.probe_profiles?.long_stream;
  if (profile === 'short') {
    const value = short ?? PROFILE_DEFAULTS.short;
    return {
      enabled: short !== undefined,
      interval: String(value.interval_s),
      shots: String(value.shots),
      timeout: String(value.timeout_s),
      minStream: '',
      maxGap: '',
    };
  }
  const value = long ?? PROFILE_DEFAULTS.long_stream;
  return {
    enabled: long !== undefined,
    interval: String(value.interval_s),
    shots: String(value.shots),
    timeout: String(value.timeout_s),
    minStream: String(value.min_stream_s),
    maxGap: String(value.max_gap_s),
  };
}

function perKind<T>(make: (kind: GuardSignalKind) => T): Readonly<Record<GuardSignalKind, T>> {
  return {
    overload: make('overload'),
    first_content_stream: make('first_content_stream'),
    first_content_nonstream: make('first_content_nonstream'),
    prelude_fail: make('prelude_fail'),
    stall: make('stall'),
    broken: make('broken'),
  };
}

export function skuDraftOf(stored: StoredGuardPolicy, legacyStreamCutoffMs: number | null): SkuDraft {
  return {
    scope: 'sku',
    signals: perKind((kind) => skuSignalDraft(kind, stored, legacyStreamCutoffMs)),
    profiles: { short: profileDraftOf(stored, 'short'), long_stream: profileDraftOf(stored, 'long_stream') },
  };
}

export function orgDraftOf(
  org: StoredGuardPolicy,
  sku: StoredGuardPolicy,
  legacyStreamCutoffMs: number | null,
): OrgDraft {
  const skuDraft = skuDraftOf(sku, legacyStreamCutoffMs);
  return {
    scope: 'org',
    signals: perKind((kind): OrgSignalDraft => {
      const section = org.signals?.[kind];
      const base = skuDraft.signals[kind];
      const custom = has(section, 'threshold_ms') || has(section, 'walk') || has(section, 'retry_after_reasoning')
        || has(section, 'chat_lane');
      const thresholdMs = section?.threshold_ms;
      const explicitNoThreshold = has(section, 'threshold_ms') && thresholdMs === null;
      return {
        disposal: custom ? 'custom' : 'inherit',
        walk: explicitNoThreshold ? 'off' : has(section, 'walk') ? (section?.walk ?? 'off') : base.walk,
        threshold: typeof thresholdMs === 'number' ? secondsText(thresholdMs) : base.threshold,
        retryAfterReasoning: has(section, 'retry_after_reasoning')
          ? section?.retry_after_reasoning === true
          : base.retryAfterReasoning,
        chatLane: has(section, 'chat_lane') ? section?.chat_lane === true : base.chatLane,
        globalTrip: section?.global_trip === 'own' ? 'own' : 'inherit',
        borrow: section?.borrow_global !== false,
        trip: section?.trip === undefined || section.trip === null ? { ...base.trip, mode: 'off' } : tripDraftOf(section.trip, kind),
      };
    }),
  };
}

export function draftOf(params: {
  readonly scope: GuardDrawerScope;
  readonly stored: StoredGuardPolicy;
  readonly skuStored: StoredGuardPolicy;
  readonly legacyStreamCutoffMs: number | null;
}): GuardDraft {
  return params.scope === 'sku'
    ? skuDraftOf(params.stored, params.legacyStreamCutoffMs)
    : orgDraftOf(params.stored, params.skuStored, params.legacyStreamCutoffMs);
}

// ---- change groups ------------------------------------------------------

export type GuardGroup =
  | `${GuardSignalKind}.disposal`
  | `${GuardSignalKind}.cap`
  | `${GuardSignalKind}.trip`
  | `${GuardSignalKind}.recover`
  | `${GuardSignalKind}.global`
  | `${GuardSignalKind}.borrow`
  | `profile.${GuardProbeProfile}`;

function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function skuDisposal(draft: SkuSignalDraft | OrgSignalDraft, kind: GuardSignalKind): unknown {
  const spec = signalView(kind).spec;
  return {
    ...(spec.walk === 'modes' ? { walk: draft.walk } : {}),
    ...(spec.threshold !== null ? { threshold: draft.threshold } : {}),
    ...(spec.retryAfterReasoning ? { retry: draft.retryAfterReasoning } : {}),
    ...(spec.chatLane ? { chat: draft.chatLane } : {}),
  };
}

export function changedGroups(draft: GuardDraft, baseline: GuardDraft): readonly GuardGroup[] {
  const out: GuardGroup[] = [];
  for (const kind of GUARD_SIGNAL_KINDS) {
    const spec = signalView(kind).spec;
    if (draft.scope === 'sku' && baseline.scope === 'sku') {
      const now = draft.signals[kind];
      const was = baseline.signals[kind];
      if (spec.walk === 'modes' && !same(skuDisposal(now, kind), skuDisposal(was, kind))) out.push(`${kind}.disposal`);
      if (spec.retryAfterCap && now.cap !== was.cap) out.push(`${kind}.cap`);
      if (!same(now.trip, was.trip)) out.push(`${kind}.trip`);
      if (now.recoverProfile !== was.recoverProfile || now.clean !== was.clean) out.push(`${kind}.recover`);
    } else if (draft.scope === 'org' && baseline.scope === 'org') {
      const now = draft.signals[kind];
      const was = baseline.signals[kind];
      const disposalNow = now.disposal === 'custom' ? skuDisposal(now, kind) : null;
      const disposalWas = was.disposal === 'custom' ? skuDisposal(was, kind) : null;
      if (spec.walk === 'modes' && !same(disposalNow, disposalWas)) out.push(`${kind}.disposal`);
      if (now.globalTrip !== was.globalTrip) out.push(`${kind}.global`);
      if (now.borrow !== was.borrow) out.push(`${kind}.borrow`);
      if (!same(now.trip, was.trip)) out.push(`${kind}.trip`);
    }
  }
  if (draft.scope === 'sku' && baseline.scope === 'sku') {
    for (const profile of GUARD_PROBE_PROFILES) {
      if (!same(draft.profiles[profile], baseline.profiles[profile])) out.push(`profile.${profile}`);
    }
  }
  return out;
}

// ---- parsing and issues -------------------------------------------------

function parseInt0(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
}

function parsePercent(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

function inRange(value: number | null, range: GuardRange): value is number {
  return value !== null && value >= range.min && value <= range.max;
}

function rangeIssue(range: GuardRange): GuardIssue {
  return { key: 'guaranteed.guard.err.range', params: { min: String(range.min), max: String(range.max) } };
}

function checkInt(issues: Map<string, GuardIssue>, path: string, text: string, range: GuardRange): void {
  const value = parseInt0(text);
  if (value === null) issues.set(path, { key: 'guaranteed.guard.err.integer', params: {} });
  else if (!inRange(value, range)) issues.set(path, rangeIssue(range));
}

function checkTrip(issues: Map<string, GuardIssue>, kind: GuardSignalKind, trip: TripDraft): void {
  if (trip.mode === 'off') return;
  checkInt(issues, `${kind}.trip.window`, trip.window, TRIP_RANGES.window_s);
  checkInt(issues, `${kind}.trip.hits`, trip.hits, TRIP_RANGES.min_hits);
  checkInt(issues, `${kind}.trip.hold`, trip.hold, TRIP_RANGES.max_hold_s);
  const ratio = parsePercent(trip.ratio);
  if (ratio === null) issues.set(`${kind}.trip.ratio`, { key: 'guaranteed.guard.err.number', params: {} });
  else if (!inRange(ratio, TRIP_RANGES.ratio_pct)) issues.set(`${kind}.trip.ratio`, rangeIssue(TRIP_RANGES.ratio_pct));
}

/** Threshold is required when the walk is armed (and always for stall, whose SKU disposal is written whole). */
function thresholdRequired(kind: GuardSignalKind, walk: GuardMode, scope: GuardDrawerScope): boolean {
  return walk !== 'off' || (kind === 'stall' && scope === 'sku');
}

function checkDisposal(
  issues: Map<string, GuardIssue>,
  kind: GuardSignalKind,
  draft: SkuSignalDraft | OrgSignalDraft,
  scope: GuardDrawerScope,
): void {
  const range = thresholdRangeSeconds(signalView(kind).spec);
  if (range === null) return;
  if (draft.threshold.trim().length === 0 && !thresholdRequired(kind, draft.walk, scope)) return;
  checkInt(issues, `${kind}.threshold`, draft.threshold, range);
}

function checkProfile(issues: Map<string, GuardIssue>, profile: GuardProbeProfile, draft: ProfileDraft): void {
  if (!draft.enabled) return;
  checkInt(issues, `profile.${profile}.interval`, draft.interval, PROFILE_RANGES.interval_s);
  checkInt(issues, `profile.${profile}.shots`, draft.shots, PROFILE_RANGES.shots);
  checkInt(
    issues,
    `profile.${profile}.timeout`,
    draft.timeout,
    profile === 'short' ? PROFILE_RANGES.short_timeout_s : PROFILE_RANGES.long_timeout_s,
  );
  if (profile === 'long_stream') {
    checkInt(issues, `profile.${profile}.minStream`, draft.minStream, PROFILE_RANGES.min_stream_s);
    checkInt(issues, `profile.${profile}.maxGap`, draft.maxGap, PROFILE_RANGES.max_gap_s);
  }
}

/** Field issues of every group this save would write, keyed by field path. */
export function draftIssues(draft: GuardDraft, baseline: GuardDraft): ReadonlyMap<string, GuardIssue> {
  const issues = new Map<string, GuardIssue>();
  const groups = new Set(changedGroups(draft, baseline));
  for (const kind of GUARD_SIGNAL_KINDS) {
    const signal = draft.signals[kind];
    if (groups.has(`${kind}.disposal`) && (draft.scope === 'sku' || draft.signals[kind].disposal === 'custom')) {
      checkDisposal(issues, kind, signal, draft.scope);
    }
    if (groups.has(`${kind}.trip`)) checkTrip(issues, kind, signal.trip);
    if (draft.scope === 'sku') {
      const sku = draft.signals[kind];
      if (groups.has(`${kind}.cap`) && sku.cap.trim().length > 0) {
        checkInt(issues, `${kind}.cap`, sku.cap, RETRY_AFTER_CAP_RANGE);
      }
      if (groups.has(`${kind}.recover`) || recoverRidesWithTrip(sku, groups.has(`${kind}.trip`))) {
        checkInt(issues, `${kind}.clean`, sku.clean, CLEAN_RANGE);
      }
    }
  }
  if (draft.scope === 'sku') {
    for (const profile of GUARD_PROBE_PROFILES) {
      if (groups.has(`profile.${profile}`)) checkProfile(issues, profile, draft.profiles[profile]);
    }
  }
  return issues;
}

// ---- patch --------------------------------------------------------------

function tripPatch(trip: TripDraft): GuardTripSpec {
  const window = parseInt0(trip.window);
  const hits = parseInt0(trip.hits);
  const hold = parseInt0(trip.hold);
  const pct = parsePercent(trip.ratio);
  const ratio = pct === null ? null : pct / 100;
  if (trip.mode === 'off') {
    return {
      mode: 'off',
      ...(inRange(window, TRIP_RANGES.window_s) ? { window_s: window } : {}),
      ...(inRange(hits, TRIP_RANGES.min_hits) ? { min_hits: hits } : {}),
      ...(pct !== null && inRange(pct, TRIP_RANGES.ratio_pct) && ratio !== null ? { min_ratio: ratio } : {}),
      ...(inRange(hold, TRIP_RANGES.max_hold_s) ? { max_hold_s: hold } : {}),
    };
  }
  return {
    mode: trip.mode,
    window_s: window ?? 0,
    min_hits: hits ?? 0,
    min_ratio: ratio ?? -1,
    max_hold_s: hold ?? 0,
  };
}

function disposalPatch(
  kind: GuardSignalKind,
  draft: SkuSignalDraft | OrgSignalDraft,
  scope: GuardDrawerScope,
): { readonly set: Record<string, unknown>; readonly unset: readonly string[] } {
  const spec = signalView(kind).spec;
  const out: Record<string, unknown> = {};
  const unset: string[] = [];
  if (spec.walk === 'modes') out.walk = draft.walk;
  if (spec.threshold !== null) {
    const seconds = parseInt0(draft.threshold);
    if (seconds !== null) out.threshold_ms = seconds * 1000;
    else if (thresholdRequired(kind, draft.walk, scope)) out.threshold_ms = -1;
    // R1-B10: an optional threshold the operator cleared is removed, not kept.
    else if (draft.threshold.trim().length === 0) unset.push('threshold_ms');
  }
  if (spec.retryAfterReasoning) out.retry_after_reasoning = draft.retryAfterReasoning;
  // SKU: off is the absent field (the stored row stays as small as before);
  // org: written either way, so an org can turn an armed SKU lane off.
  if (spec.chatLane) {
    if (draft.chatLane || scope === 'org') out.chat_lane = draft.chatLane;
    else unset.push('chat_lane');
  }
  return { set: out, unset };
}

/** Arming a trip on a SKU with no stored recovery writes the recovery shown beside it. */
function recoverRidesWithTrip(draft: SkuSignalDraft, tripChanged: boolean): boolean {
  return tripChanged && draft.trip.mode !== 'off';
}

function skuSignalPatch(
  kind: GuardSignalKind,
  draft: SkuSignalDraft,
  stored: StoredGuardPolicy,
  groups: ReadonlySet<GuardGroup>,
): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = {};
  const unset: string[] = [];
  if (groups.has(`${kind}.disposal`)) {
    const disposal = disposalPatch(kind, draft, 'sku');
    Object.assign(out, disposal.set);
    unset.push(...disposal.unset);
  }
  if (groups.has(`${kind}.cap`)) {
    const cap = parseInt0(draft.cap);
    if (draft.cap.trim().length === 0) unset.push('retry_after_cap_s');
    else out.retry_after_cap_s = cap ?? -1;
  }
  if (groups.has(`${kind}.trip`)) out.trip = tripPatch(draft.trip);
  const storedRecover = stored.signals?.[kind]?.recover;
  const recoverNeeded = groups.has(`${kind}.recover`)
    || (recoverRidesWithTrip(draft, groups.has(`${kind}.trip`)) && (storedRecover === undefined || storedRecover === null));
  if (recoverNeeded) out.recover = { profile: draft.recoverProfile, clean: parseInt0(draft.clean) ?? 0 };
  if (unset.length > 0) out.unset = unset;
  return Object.keys(out).length > 0 ? out : undefined;
}

function orgSignalPatch(
  kind: GuardSignalKind,
  draft: OrgSignalDraft,
  groups: ReadonlySet<GuardGroup>,
): Record<string, unknown> | undefined {
  const spec = signalView(kind).spec;
  const out: Record<string, unknown> = {};
  const unset: string[] = [];
  if (groups.has(`${kind}.disposal`)) {
    if (draft.disposal === 'inherit') {
      if (spec.walk === 'modes') unset.push('walk');
      if (spec.threshold !== null) unset.push('threshold_ms');
      if (spec.retryAfterReasoning) unset.push('retry_after_reasoning');
      if (spec.chatLane) unset.push('chat_lane');
    } else {
      const disposal = disposalPatch(kind, draft, 'org');
      Object.assign(out, disposal.set);
      unset.push(...disposal.unset);
    }
  }
  if (groups.has(`${kind}.global`)) {
    if (draft.globalTrip === 'own') out.global_trip = 'own';
    else unset.push('global_trip');
  }
  if (groups.has(`${kind}.borrow`)) {
    if (draft.borrow) unset.push('borrow_global');
    else out.borrow_global = false;
  }
  if (groups.has(`${kind}.trip`)) {
    if (draft.trip.mode === 'off') unset.push('trip');
    else out.trip = tripPatch(draft.trip);
  }
  if (unset.length > 0) out.unset = unset;
  return Object.keys(out).length > 0 ? out : undefined;
}

function profilePatch(profile: GuardProbeProfile, draft: ProfileDraft): Record<string, unknown> | null {
  if (!draft.enabled) return null;
  const base = {
    interval_s: parseInt0(draft.interval) ?? 0,
    shots: parseInt0(draft.shots) ?? 0,
    timeout_s: parseInt0(draft.timeout) ?? 0,
  };
  if (profile === 'short') return base;
  return { ...base, min_stream_s: parseInt0(draft.minStream) ?? 0, max_gap_s: parseInt0(draft.maxGap) ?? 0 };
}

/** The `policy` patch for this save: only the groups that differ from `baseline`. */
export function guardPolicyPatch(
  draft: GuardDraft,
  baseline: GuardDraft,
  stored: StoredGuardPolicy,
): GuardPolicyPatchBody {
  const groups = new Set(changedGroups(draft, baseline));
  const signals: Partial<Record<GuardSignalKind, Record<string, unknown>>> = {};
  for (const kind of GUARD_SIGNAL_KINDS) {
    const section = draft.scope === 'sku'
      ? skuSignalPatch(kind, draft.signals[kind], stored, groups)
      : orgSignalPatch(kind, draft.signals[kind], groups);
    if (section !== undefined) signals[kind] = section;
  }
  const profiles: Partial<Record<GuardProbeProfile, Record<string, unknown> | null>> = {};
  if (draft.scope === 'sku') {
    for (const profile of GUARD_PROBE_PROFILES) {
      if (groups.has(`profile.${profile}`)) profiles[profile] = profilePatch(profile, draft.profiles[profile]);
    }
  }
  return {
    ...(Object.keys(signals).length > 0 ? { signals } : {}),
    ...(Object.keys(profiles).length > 0 ? { probe_profiles: profiles } : {}),
  };
}

export function guardPutBody(
  draft: GuardDraft,
  baseline: GuardDraft,
  stored: StoredGuardPolicy,
  ids: { readonly skuId: string; readonly orgId: string | null },
): GuardPutBody {
  const policy = guardPolicyPatch(draft, baseline, stored);
  return draft.scope === 'org' && ids.orgId !== null
    ? { scope: 'org_sku', sku_id: ids.skuId, org_id: ids.orgId, policy }
    : { scope: 'sku', sku_id: ids.skuId, policy };
}

/** The stored policy this save would produce, by the write face's own merge and checks. */
export function previewPolicy(
  stored: StoredGuardPolicy,
  body: GuardPutBody,
): GuardParseResult<StoredGuardPolicy> {
  return applyGuardPolicyPatch(stored, body.policy, body.scope);
}

// ---- display ------------------------------------------------------------

/** Org rows count overlay fields: every signal field the org row sets. */
export function overlayFieldCount(policy: StoredGuardPolicy): number {
  let count = 0;
  for (const section of Object.values(policy.signals ?? {})) count += Object.keys(section).length;
  return count;
}

export interface GuardSummaryChip {
  readonly kind: GuardSignalKind;
  readonly part: 'walk' | 'trip';
  readonly mode: 'shadow' | 'act';
  readonly seconds: number | null;
}

export function skuSummaryChips(
  sku: StoredGuardPolicy,
  legacyStreamCutoffMs: number | null,
): readonly GuardSummaryChip[] {
  const resolved = resolveGuardPolicy({ sku });
  const chips: GuardSummaryChip[] = [];
  for (const view of GUARD_SIGNAL_VIEWS) {
    const kind = view.spec.kind;
    const signal = resolved?.signals[kind];
    if (signal?.walk !== undefined) {
      chips.push({
        kind,
        part: 'walk',
        mode: signal.walk,
        seconds: signal.threshold_ms === undefined ? null : signal.threshold_ms / 1000,
      });
    } else if (kind === 'first_content_stream' && streamFromLegacy(sku, legacyStreamCutoffMs)) {
      const ms = resolveNoResponseCutoffMs({ sku, legacyStreamCutoffMs });
      if (ms !== null) chips.push({ kind, part: 'walk', mode: 'act', seconds: ms / 1000 });
    }
    if (signal?.trip !== undefined) chips.push({ kind, part: 'trip', mode: signal.trip.mode, seconds: null });
  }
  return chips;
}

/** A signal whose trip is armed (SKU or this org) but whose recovery profile is not enabled. */
export function profileMissing(draft: SkuDraft, kind: GuardSignalKind): boolean {
  const signal = draft.signals[kind];
  return signal.trip.mode !== 'off' && !draft.profiles[signal.recoverProfile].enabled;
}
